import { Button, Spinner, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { SharedWithMeConversation } from '../../types';

export function SharedTab({
    sharedWithMe,
    isLoading,
    currentConversationId,
    onOpen,
    onDuplicate,
    formatSharedByName,
}: {
    sharedWithMe: SharedWithMeConversation[];
    isLoading: boolean;
    currentConversationId: string | null;
    onOpen: (id: string, reportId?: number) => void;
    onDuplicate: (id: string) => Promise<void>;
    formatSharedByName: (sharedBy: SharedWithMeConversation['sharedBy']) => string;
}) {
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <Spinner size="lg" color="primary" />
            </div>
        );
    }

    if (sharedWithMe.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-default-100">
                    <Icon icon="lucide:users" width={28} className="text-default-300" />
                </div>
                <p className="mt-4 text-base font-medium text-default-600">
                    No shared conversations
                </p>
                <p className="mt-1 text-small text-default-400">
                    Conversations shared with you will appear here
                </p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-default-200 rounded-xl border border-default-200">
            {sharedWithMe.map((shared) => {
                const isActive = currentConversationId === shared.conversationId;
                return (
                    <div
                        key={shared.conversationId}
                        className={`group flex items-center gap-3 px-4 py-2.5 transition-colors ${isActive ? 'bg-default-100' : 'hover:bg-default-50'}`}
                    >
                        <div
                            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
                            onClick={() => onOpen(shared.conversationId, shared.reportId)}
                        >
                            <Icon
                                icon={
                                    shared.reportId ? 'lucide:file-text' : 'lucide:message-square'
                                }
                                width={15}
                                className="shrink-0 text-default-400"
                            />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-small font-medium text-foreground">
                                    {shared.title}
                                </p>
                                <p className="mt-0.5 flex items-center gap-1 text-mini text-default-400">
                                    <Icon icon="lucide:user" width={10} />
                                    <span className="truncate">
                                        {formatSharedByName(shared.sharedBy)}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="opacity-0 transition-opacity group-hover:opacity-100">
                            <Tooltip content="Duplicate to my chats" size="sm">
                                <Button
                                    isIconOnly
                                    variant="light"
                                    size="sm"
                                    radius="md"
                                    className="h-7 w-7 min-w-7"
                                    onPress={() => onDuplicate(shared.conversationId)}
                                    aria-label="Duplicate conversation"
                                >
                                    <Icon
                                        icon="lucide:copy"
                                        width={14}
                                        className="text-default-500"
                                    />
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
